import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import { Container, Table, Spinner, Card, CardBody } from "reactstrap";
import { Link } from "react-router-dom";
import { DashboardPageHeader, TableSkeleton } from "../../Components/Common/DashboardKit";
import FeatherIcon from "feather-icons-react";
import api from "../../api";
import { getPunchLocation } from "../../utils/terminalMap";

const ROWS_PER_PAGE = 7;
const DAY_NAMES = ["SUN", "MON", "TUES", "WED", "THURS", "FRI", "SAT"];

const PunchRecords = () => {
    document.title = "My Time Punch Records | SSW Dashboard";

    const authUser = sessionStorage.getItem("authUser");
    const employeeId = authUser ? JSON.parse(authUser)?.uid : null;
    const empName = authUser ? JSON.parse(authUser)?.name : "";

    // ── State ──────────────────────────────────────────────────
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    // Round 27 — date range + view mode mirror into the URL (shareable).
    const [searchParams, setSearchParams] = useSearchParams();
    const [viewMode, setViewMode] = useState(() => searchParams.get("view") || "monthly");
    const [page, setPage] = useState(1);

    const [fromDate, setFromDate] = useState(() => {
        if (searchParams.get("from")) return searchParams.get("from");
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1)
            .toISOString().split("T")[0];
    });
    const [toDate, setToDate] = useState(
        () => searchParams.get("to") || new Date().toISOString().split("T")[0]
    );
    useEffect(() => {
        setSearchParams({ from: fromDate, to: toDate, view: viewMode }, { replace: true });
    }, [fromDate, toDate, viewMode]);

    // ── Fetch ──────────────────────────────────────────────────
    const fetchRecords = async (from, to) => {
        if (!employeeId) return;
        setLoading(true);
        try {
            const res = await api.get(
                `WhosInBuilding/punch-records/${employeeId}`,
                { params: { from: from ?? fromDate, to: to ?? toDate } }
            );
            setRecords(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Error fetching punch records:", err);
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchRecords(); }, []);

    // ── Quick range helpers ────────────────────────────────────
    const applyRange = (from, to) => {
        setFromDate(from);
        setToDate(to);
        setPage(1);
        fetchRecords(from, to);
    };

    const quickRanges = [
        {
            label: "This Month",
            fn: () => {
                const d = new Date();
                applyRange(
                    new Date(d.getFullYear(), d.getMonth(), 1)
                        .toISOString().split("T")[0],
                    d.toISOString().split("T")[0]
                );
            }
        },
        {
            label: "Last Month",
            fn: () => {
                const d = new Date();
                applyRange(
                    new Date(d.getFullYear(), d.getMonth() - 1, 1)
                        .toISOString().split("T")[0],
                    new Date(d.getFullYear(), d.getMonth(), 0)
                        .toISOString().split("T")[0]
                );
            }
        },
        {
            label: "This Week",
            fn: () => {
                const d = new Date();
                const monday = new Date(d);
                monday.setDate(d.getDate() - (d.getDay() === 0 ? 6 : d.getDay() - 1));
                applyRange(
                    monday.toISOString().split("T")[0],
                    d.toISOString().split("T")[0]
                );
            }
        },
    ];

    // ── Build date list ────────────────────────────────────────
    const allDays = [];
    const startD = new Date(fromDate + "T00:00:00");
    const endD = new Date(toDate + "T00:00:00");
    for (let d = new Date(startD); d <= endD; d.setDate(d.getDate() + 1)) {
        allDays.push(new Date(d).toISOString().split("T")[0]);
    }

    // Group punches by date
    const punchMap = records.reduce((acc, r) => {
        const key = r.reportDate
            ? new Date(r.reportDate).toISOString().split("T")[0]
            : null;
        if (key) {
            if (!acc[key]) acc[key] = [];
            acc[key].push(r);
        }
        return acc;
    }, {});

    // Weekly groups (Mon–Sun)
    const weekGroups = [];
    let week = [];
    allDays.forEach((d, i) => {
        week.push(d);
        const dow = new Date(d + "T00:00:00").getDay();
        if (dow === 6 || i === allDays.length - 1) {
            weekGroups.push([...week]);
            week = [];
        }
    });

    // Pagination
    const isWeekly = viewMode === "weekly";
    const totalPages = isWeekly
        ? weekGroups.length
        : Math.ceil(allDays.length / ROWS_PER_PAGE);

    const currentDays = isWeekly
        ? (weekGroups[page - 1] || [])
        : allDays.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

    const weekLabel = isWeekly && currentDays.length > 0
        ? `Week of ${new Date(currentDays[0] + "T00:00:00")
            .toLocaleDateString("en-US", { month: "short", day: "numeric" })} — 
           ${new Date(currentDays[currentDays.length - 1] + "T00:00:00")
            .toLocaleDateString("en-US", {
                month: "short", day: "numeric", year: "numeric"
            })}`
        : null;

    // ── Summary stats ──────────────────────────────────────────
    const presentDays = allDays.filter(d => (punchMap[d] || []).length > 0).length;
    const absentDays = allDays.filter(d => {
        const dow = new Date(d + "T00:00:00").getDay();
        return (punchMap[d] || []).length === 0 && dow !== 0 && dow !== 6;
    }).length;
    const weekendDays = allDays.filter(d => {
        const dow = new Date(d + "T00:00:00").getDay();
        return dow === 0 || dow === 6;
    }).length;
    const totalPunches = records.length;

    // ── Pagination buttons ─────────────────────────────────────
    const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
        .reduce((acc, p, i, arr) => {
            if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
            acc.push(p);
            return acc;
        }, []);

    const btnBase = {
        padding: "5px 10px",
        borderRadius: "6px",
        border: "1px solid var(--color-border-tertiary)",
        background: "var(--color-background-primary)",
        color: "var(--color-text-primary)",
        cursor: "pointer",
        fontSize: "12px"
    };
    const btnDisabled = { ...btnBase, color: "var(--color-text-secondary)", cursor: "not-allowed" };
    const btnActive = { ...btnBase, background: "#534AB7", color: "#fff", fontWeight: 600, border: "1px solid #534AB7" };

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-time-line"
                    eyebrow="Attendance"
                    title="My Time Punch Records"
                    subtitle="Your clock-in / clock-out history."
                    tone="blue"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "Attendance", to: "/workflows?wf=pac" },
                        { label: "My Time Punch Records" },
                    ]}
                />

                {/* ── Summary cards ──────────────────────────── */}
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                    gap: "12px",
                    marginBottom: "20px"
                }}>
                    {[
                        { label: "Total Days", value: allDays.length, color: "#534AB7", icon: "ri-calendar-2-line" },
                        { label: "Present", value: presentDays, color: "#198754", icon: "ri-checkbox-circle-line" },
                        { label: "Absent", value: absentDays, color: "#dc3545", icon: "ri-close-circle-line" },
                        { label: "Weekends", value: weekendDays, color: "#6c757d", icon: "ri-rest-time-line" },
                        { label: "Total Punches", value: totalPunches, color: "#0d6efd", icon: "ri-fingerprint-line" },
                    ].map(({ label, value, color, icon }) => (
                        <div key={label} style={{
                            background: "var(--color-background-primary)",
                            border: "0.5px solid var(--color-border-tertiary)",
                            borderRadius: "10px",
                            padding: "14px 16px",
                            borderTop: `3px solid ${color}`
                        }}>
                            <div style={{
                                fontSize: "12px",
                                color: "var(--color-text-secondary)",
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                                marginBottom: "6px"
                            }}>
                                <i className={icon} style={{ color }} />
                                {label}
                            </div>
                            <div style={{
                                fontSize: "24px",
                                fontWeight: 600,
                                color: color
                            }}>
                                {loading ? <Spinner size="sm" /> : value}
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Main card ──────────────────────────────── */}
                <Card>
                    <CardBody>

                        {/* ── Controls ── */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            marginBottom: "20px",
                            flexWrap: "wrap"
                        }}>
                            {/* Range label */}
                            <span style={{
                                fontWeight: 600,
                                fontSize: "13px",
                                color: "var(--color-text-secondary)"
                            }}>
                                <i className="ri-calendar-line me-1" />
                                Range:
                            </span>

                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                style={{
                                    fontSize: "13px",
                                    padding: "5px 8px",
                                    borderRadius: "6px",
                                    border: "1px solid var(--color-border-tertiary)",
                                    background: "var(--color-background-primary)",
                                    color: "var(--color-text-primary)"
                                }}
                            />
                            <span style={{
                                fontSize: "12px",
                                color: "var(--color-text-secondary)"
                            }}>
                                to
                            </span>
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                style={{
                                    fontSize: "13px",
                                    padding: "5px 8px",
                                    borderRadius: "6px",
                                    border: "1px solid var(--color-border-tertiary)",
                                    background: "var(--color-background-primary)",
                                    color: "var(--color-text-primary)"
                                }}
                            />

                            <button
                                onClick={() => {
                                    setPage(1);
                                    fetchRecords();
                                }}
                                style={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    padding: "6px 14px",
                                    borderRadius: "6px",
                                    border: "none",
                                    background: "#534AB7",
                                    color: "#fff",
                                    cursor: "pointer"
                                }}
                            >
                                <i className="ri-search-line me-1" />
                                Search
                            </button>

                            {/* Quick ranges */}
                            {quickRanges.map(({ label, fn }) => (
                                <button
                                    key={label}
                                    onClick={fn}
                                    style={{
                                        fontSize: "11px",
                                        padding: "5px 12px",
                                        borderRadius: "20px",
                                        border: "1px solid var(--color-border-tertiary)",
                                        background: "var(--color-background-primary)",
                                        color: "var(--color-text-secondary)",
                                        cursor: "pointer"
                                    }}
                                >
                                    {label}
                                </button>
                            ))}

                            {/* View toggle */}
                            <div style={{
                                marginLeft: "auto",
                                display: "flex",
                                border: "1px solid var(--color-border-tertiary)",
                                borderRadius: "8px",
                                overflow: "hidden"
                            }}>
                                {/* Round 27 — CSV of the loaded punch rows */}
                                <button
                                    type="button"
                                    style={btnBase}
                                    onClick={() => {
                                        const cols = records.length
                                            ? Object.keys(records[0]).map((k) => ({ key: k, label: k }))
                                            : [];
                                        exportRowsToCsv(`punch-records-${fromDate}_${toDate}.csv`, cols, records);
                                    }}
                                    title="Download the loaded rows as CSV"
                                >
                                    ⬇ Export CSV ({records.length})
                                </button>
                                {["monthly", "weekly"].map((mode) => (
                                    <button
                                        key={mode}
                                        onClick={() => {
                                            setViewMode(mode);
                                            setPage(1);
                                        }}
                                        style={{
                                            fontSize: "12px",
                                            fontWeight: viewMode === mode ? 600 : 400,
                                            padding: "6px 14px",
                                            border: "none",
                                            background: viewMode === mode
                                                ? "#534AB7"
                                                : "var(--color-background-primary)",
                                            color: viewMode === mode
                                                ? "#fff"
                                                : "var(--color-text-secondary)",
                                            cursor: "pointer"
                                        }}
                                    >
                                        {mode === "monthly"
                                            ? <><i className="ri-calendar-2-line me-1" />Monthly</>
                                            : <><i className="ri-calendar-line me-1" />Weekly</>
                                        }
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* ── Report header ── */}
                        {!loading && records.length > 0 && (
                            <div style={{
                                padding: "10px 14px",
                                marginBottom: "14px",
                                borderLeft: "3px solid #534AB7",
                                borderRadius: "0 4px 4px 0",
                                background: "var(--color-background-secondary)",
                                fontSize: "13px",
                                color: "var(--color-text-secondary)"
                            }}>
                                <div>
                                    <strong style={{ color: "var(--color-text-primary)" }}>
                                        Employee:
                                    </strong>{" "}
                                    {empName} — ID: {employeeId}
                                </div>
                                <div>
                                    <strong style={{ color: "var(--color-text-primary)" }}>
                                        Print Date:
                                    </strong>{" "}
                                    {new Date().toLocaleDateString("en-US", {
                                        month: "long", day: "numeric", year: "numeric"
                                    })}{" "}
                                    {new Date().toLocaleTimeString()}
                                </div>
                                <div>
                                    <strong style={{ color: "var(--color-text-primary)" }}>
                                        Report Range:
                                    </strong>{" "}
                                    {new Date(fromDate + "T00:00:00").toLocaleDateString("en-US", {
                                        month: "short", day: "numeric", year: "numeric"
                                    })}{" "}
                                    —{" "}
                                    {new Date(toDate + "T00:00:00").toLocaleDateString("en-US", {
                                        month: "short", day: "numeric", year: "numeric"
                                    })}
                                    {isWeekly && weekLabel && (
                                        <span style={{
                                            marginLeft: "12px",
                                            padding: "2px 8px",
                                            borderRadius: "20px",
                                            background: "#534AB715",
                                            color: "#534AB7",
                                            fontSize: "12px",
                                            fontWeight: 600
                                        }}>
                                            {weekLabel}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── Table ── */}
                        {loading ? (
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <tbody><TableSkeleton rows={8} columns={7} /></tbody>
                                </table>
                            </div>
                        ) : records.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <i className="ri-file-list-3-line fs-1 d-block mb-3" />
                                No punch records found for selected range.
                            </div>
                        ) : (
                            <>
                                <Table bordered hover responsive className="dk-sticky-thead"
                                    className="align-middle mb-0"
                                    style={{ fontSize: "13px" }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 65 }}>Day</th>
                                            <th style={{ width: 105 }}>Date</th>
                                            <th>Clock Time</th>
                                            <th style={{ width: 85 }}>Type</th>
                                            <th style={{ width: 75 }}>Status</th>
                                            <th>Location</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentDays.map((dateStr) => {
                                            const punches = punchMap[dateStr] || [];
                                            const dateObj = new Date(dateStr + "T00:00:00");
                                            const dayName = DAY_NAMES[dateObj.getDay()];
                                            const isWeekend = dateObj.getDay() === 0
                                                || dateObj.getDay() === 6;
                                            const dateLabel = dateObj.toLocaleDateString(
                                                "en-US", {
                                                month: "numeric",
                                                day: "numeric",
                                                year: "numeric"
                                            }
                                            );

                                            if (punches.length === 0) {
                                                return (
                                                    <tr key={dateStr} style={{
                                                        background: isWeekend
                                                            ? "var(--color-background-secondary)"
                                                            : "#fff8f8"
                                                    }}>
                                                        <td style={{ fontWeight: 600, color: "var(--color-text-secondary)" }}>
                                                            {dayName}
                                                        </td>
                                                        <td style={{ color: "var(--color-text-secondary)" }}>
                                                            {dateLabel}
                                                        </td>
                                                        <td colSpan={4}>
                                                            <span style={{
                                                                fontSize: "12px",
                                                                fontWeight: 500,
                                                                color: isWeekend
                                                                    ? "var(--color-text-secondary)"
                                                                    : "#dc3545"
                                                            }}>
                                                                {isWeekend ? "Weekend" : "ABSENT"}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            return punches.map((record, idx) => {
                                                const loc = getPunchLocation(
                                                    record.terminalId ?? record.terminal_id,
                                                    record.computer
                                                );
                                                return (
                                                    <tr key={`${dateStr}-${idx}`}>
                                                        {idx === 0 && (
                                                            <>
                                                                <td rowSpan={punches.length}
                                                                    style={{
                                                                        fontWeight: 700,
                                                                        verticalAlign: "top",
                                                                        paddingTop: "10px"
                                                                    }}>
                                                                    {dayName}
                                                                </td>
                                                                <td rowSpan={punches.length}
                                                                    style={{
                                                                        verticalAlign: "top",
                                                                        paddingTop: "10px"
                                                                    }}>
                                                                    {dateLabel}
                                                                </td>
                                                            </>
                                                        )}

                                                        <td className="font-monospace fw-semibold">
                                                            {record.clockTime
                                                                ? new Date(record.clockTime)
                                                                    .toLocaleTimeString("en-US", {
                                                                        hour: "2-digit",
                                                                        minute: "2-digit",
                                                                        second: "2-digit"
                                                                    })
                                                                : "-"}
                                                        </td>

                                                        <td style={{
                                                            fontSize: "12px",
                                                            color: "var(--color-text-secondary)"
                                                        }}>
                                                            {record.clockType || "Normal"}
                                                        </td>

                                                        <td>
                                                            <span className={`badge ${record.status === "IN"
                                                                ? "bg-success"
                                                                : "bg-danger"
                                                                }`}>
                                                                {record.status || "-"}
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <span style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: "5px",
                                                                padding: "3px 10px",
                                                                borderRadius: "20px",
                                                                fontSize: "12px",
                                                                fontWeight: 500,
                                                                background: `${loc.color}15`,
                                                                color: loc.color,
                                                                border: `1px solid ${loc.color}35`,
                                                            }}>
                                                                <i className={loc.icon} />
                                                                {loc.label}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                        })}
                                    </tbody>
                                </Table>

                                {/* ── Pagination ── */}
                                {totalPages > 1 && (
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        marginTop: "16px",
                                        paddingTop: "12px",
                                        borderTop: "0.5px solid var(--color-border-tertiary)"
                                    }}>
                                        <span style={{
                                            fontSize: "13px",
                                            color: "var(--color-text-secondary)"
                                        }}>
                                            {isWeekly
                                                ? `Week ${page} of ${totalPages}`
                                                : `Days ${(page - 1) * ROWS_PER_PAGE + 1}–${Math.min(page * ROWS_PER_PAGE, allDays.length)
                                                } of ${allDays.length}`
                                            }
                                        </span>

                                        <div style={{ display: "flex", gap: "4px" }}>
                                            <button
                                                onClick={() => setPage(1)}
                                                disabled={page === 1}
                                                style={page === 1 ? btnDisabled : btnBase}
                                            >
                                                <i className="ri-skip-back-line" />
                                            </button>

                                            <button
                                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                                disabled={page === 1}
                                                style={page === 1 ? btnDisabled : btnBase}
                                            >
                                                <i className="ri-arrow-left-s-line" />
                                                Prev
                                            </button>

                                            {pageNumbers.map((p, i) =>
                                                p === "..." ? (
                                                    <span key={`dots-${i}`}
                                                        style={{
                                                            padding: "5px 6px",
                                                            fontSize: "12px",
                                                            color: "var(--color-text-secondary)"
                                                        }}>
                                                        …
                                                    </span>
                                                ) : (
                                                    <button
                                                        key={p}
                                                        onClick={() => setPage(p)}
                                                        style={page === p ? btnActive : btnBase}
                                                    >
                                                        {p}
                                                    </button>
                                                )
                                            )}

                                            <button
                                                onClick={() => setPage(p =>
                                                    Math.min(totalPages, p + 1)
                                                )}
                                                disabled={page === totalPages}
                                                style={page === totalPages ? btnDisabled : btnBase}
                                            >
                                                Next
                                                <i className="ri-arrow-right-s-line ms-1" />
                                            </button>

                                            <button
                                                onClick={() => setPage(totalPages)}
                                                disabled={page === totalPages}
                                                style={page === totalPages ? btnDisabled : btnBase}
                                            >
                                                <i className="ri-skip-forward-line" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default PunchRecords;